import {
  AriaRadioGroup,
  HStack,
  KdsCardSelectable,
  KdsCheckbox,
  KdsModal,
  KdsText,
  theme,
  VStack,
} from "@kazaar-platform/design-system";
import { T, useTranslate } from "@tolgee/react";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { OPENAI_TERMS_OF_USE_URL } from "@/constants/routes.constants";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission";
import { openBriefAITermsAcceptedAtom } from "@/store";
import { BriefMode } from "./ModalBriefMode.constants";
import { type ModalBriefModeProps } from "./ModalBriefMode.types";

const isBriefMode = (value: string): value is BriefMode => {
  return value === BriefMode.AI || value === BriefMode.Manual;
};

export const ModalBriefMode = ({
  isOpen,
  onOpenChange,
  isOnlyAcceptAI,
}: ModalBriefModeProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const { hasBrokerKazaarRole } = useRolePermission();

  const [consentCheckboxChecked, setConsentCheckboxChecked] = useState(false);
  const [briefModeSelected, setBriefModeSelected] = useState<BriefMode>(
    BriefMode.AI
  );

  const setIsIATermsAndConditionsAccepted = useSetAtom(
    openBriefAITermsAcceptedAtom
  );

  const isPending = false;

  const isSubmitDisabled =
    isPending ||
    (briefModeSelected === BriefMode.AI && !consentCheckboxChecked);

  const handleClose = () => {
    if (isOnlyAcceptAI) {
      return onOpenChange();
    }

    void navigate(hasBrokerKazaarRole ? "/orders" : "/catalog");
    onOpenChange();
  };

  const handleSubmit = () => {
    setIsIATermsAndConditionsAccepted(briefModeSelected === BriefMode.AI);
    onOpenChange();
  };

  const handleBriefModeChange = (value: string) => {
    if (isBriefMode(value)) {
      setBriefModeSelected(value);
    }
  };

  const getBackButtonLabel = () => {
    if (isOnlyAcceptAI) {
      return t("buttons.cancel");
    }
    return hasBrokerKazaarRole
      ? t("buttons.backToOrders")
      : t("buttons.backToCatalog");
  };

  return (
    <KdsModal
      isOpen={isOpen}
      isDismissable={false}
      size="lg"
      onOpenChange={handleClose}
      dialogProps={{
        dialogHeaderProps: {
          title: isOnlyAcceptAI
            ? t("openBrief.modalBriefMode.titleAIOnly")
            : t("openBrief.modalBriefMode.title"),
        },
        content: (
          <VStack gap="sp-xl">
            {!isOnlyAcceptAI && (
              <AriaRadioGroup
                onChange={handleBriefModeChange}
                value={briefModeSelected}
              >
                <HStack gap="sp-xs">
                  <KdsCardSelectable
                    direction="vertical"
                    title={t("openBrief.modalBriefMode.card.titleAI")}
                    value={BriefMode.AI}
                    onChange={() => handleBriefModeChange(BriefMode.AI)}
                    tagV2Props={{
                      children: t("openBrief.modalBriefMode.card.tagAI"),
                      variant: "alpha",
                    }}
                  >
                    {/* // TODO : Replace whitespace-pre-line by KdsText when available */}
                    <div className="text-left whitespace-pre-line">
                      <T
                        keyName="openBrief.modalBriefMode.card.descriptionAI"
                        params={{
                          b: <b />,
                          br: <br />,
                        }}
                      />
                    </div>
                  </KdsCardSelectable>

                  <KdsCardSelectable
                    direction="vertical"
                    title={t("openBrief.modalBriefMode.card.titleManual")}
                    value={BriefMode.Manual}
                    onChange={() => handleBriefModeChange(BriefMode.Manual)}
                  >
                    <div className="text-left whitespace-pre-line">
                      <T
                        keyName="openBrief.modalBriefMode.card.descriptionManual"
                        params={{
                          b: <b />,
                          br: <br />,
                        }}
                      />
                    </div>
                  </KdsCardSelectable>
                </HStack>
              </AriaRadioGroup>
            )}

            {briefModeSelected === BriefMode.AI && (
              <VStack gap="sp-sm">
                <KdsCheckbox
                  required
                  isChecked={consentCheckboxChecked}
                  label={t("openBrief.modalBriefMode.checkboxLabel")}
                  onChange={(e) => setConsentCheckboxChecked(e)}
                />

                <KdsText size="caption-sm" color="text-bodyvariant">
                  <T
                    keyName="openBrief.modalBriefMode.checkboxDescription"
                    params={{
                      a: (
                        // biome-ignore lint/a11y/useAnchorContent: content injected by Tolgee <T> component
                        <a
                          href={OPENAI_TERMS_OF_USE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: theme.colors.primary.main,
                            fontSize: theme.caption.sm,
                            textDecoration: "underline",
                          }}
                        />
                      ),
                    }}
                  />
                </KdsText>
              </VStack>
            )}
          </VStack>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: getBackButtonLabel(),
              color: "neutral",
              variant: "ghost",
              isLoading: isPending,
              disabled: isPending,
              onPress: handleClose,
            },
            {
              disabled: isSubmitDisabled,
              isLoading: isPending,
              children: isOnlyAcceptAI
                ? t("buttons.activeSmartAssistant")
                : t("buttons.startBrief"),
              onPress: handleSubmit,
            },
          ],
        },
      }}
    />
  );
};
